import type { ItemDefinition, MaterialTag } from "./types";
export type DropMethod =
  | "carve_body"
  | "carve_tail"
  | "capture"
  | "target_reward"
  | "break_part"
  | "shiny_drop";
export interface DropEntry {
  materialKey: string;
  name: string;
  weightPercent: number;
  quantity: number;
  rarity: number;
  tags: MaterialTag[];
}
export interface DropPool {
  method: DropMethod;
  rolls: number;
  partKey?: string;
  entries: DropEntry[];
}
export interface DropTable {
  monsterId: string;
  tier: number;
  pools: DropPool[];
}
export function seededRandom(seed: string) {
  let h = 2166136261;
  for (const c of seed) {
    h ^= c.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function rollPool(pool: DropPool, rng: () => number): DropEntry[] {
  const total = pool.entries.reduce((n, e) => n + e.weightPercent, 0);
  return Array.from({ length: pool.rolls }, () => {
    let roll = rng() * total;
    return (
      pool.entries.find((e) => (roll -= e.weightPercent) <= 0) ??
      pool.entries.at(-1)!
    );
  });
}
const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
    .replace(/^-|-$/g, "");
export function buildFallbackTable(monster: {
  id: string;
  name: string;
  star: number;
  elements?: string[];
}): DropTable {
  const tier = Math.ceil(monster.star / 2);
  const tag = `monster:${monster.id}` as MaterialTag;
  const base = (
    suffix: string,
    name: string,
    part: MaterialTag,
    weight: number,
    rarity = tier,
  ): DropEntry => ({
    materialKey: `monster.${slug(monster.id)}.${suffix}`,
    name: `${monster.name}${name}`,
    weightPercent: weight,
    quantity: 1,
    rarity,
    tags: [tag, part, `tier:${tier}`],
  });
  const scale = base("scale", "鳞", "part:scale", 55);
  const shell = base("shell", "甲壳", "part:shell", 35);
  const rare = base(
    "rare",
    "稀有素材",
    "part:rare",
    10,
    Math.min(10, tier + 2),
  );
  return {
    monsterId: monster.id,
    tier,
    pools: [
      { method: "carve_body", rolls: 3, entries: [scale, shell, rare] },
      {
        method: "carve_tail",
        rolls: 1,
        partKey: "tail",
        entries: [base("tail", "尾", "part:tail", 80), rare],
      },
      { method: "capture", rolls: 3, entries: [shell, scale, rare] },
      { method: "target_reward", rolls: 3, entries: [scale, shell, rare] },
    ],
  };
}
export function materialDefinition(
  entry: DropEntry,
  monsterId: string,
): ItemDefinition {
  return {
    key: entry.materialKey,
    name: entry.name,
    description: "由公会确认并登记的怪物素材。",
    meta: {
      kind: "material",
      rarity: entry.rarity,
      tags: entry.tags,
      monsterId,
    },
  };
}
